import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import type { KtInsightsReportPage } from '@/types';

/**
 * Choose a KT Insights report, then tick the pages to send — by name.
 *
 * This replaced five preset tiles held in code. Every page of every report is now offered, because
 * the list comes from `Embed.Page` on MANDW01 rather than from a curated array: the tiles could
 * only express the combinations somebody had thought to add, and adding one needed a deploy.
 *
 * What is stored is unchanged — a CSV of `DefaultPageOrder` ids, exactly what PBRS.Queue takes —
 * so this is a change to how an export is chosen, not to how it is delivered.
 */
export function KtInsightsPagePicker({ reportId, selected, onChange, entitlement }: {
  reportId: number | null;
  selected: number[];
  onChange: (reportId: number | null, pages: number[]) => void;
  /** What the chosen recipient may actually receive; see {@link PageEntitlement}. */
  entitlement?: PageEntitlement;
}) {
  const api = useApi();

  const { data: reports, isLoading: loadingReports } = useQuery({
    queryKey: ['ktinsights-reports'],
    queryFn: api.ktInsightsReports
  });

  const { data: pages, isFetching: loadingPages } = useQuery({
    queryKey: ['ktinsights-report-pages', reportId],
    queryFn: () => api.ktInsightsReportPages(reportId!),
    enabled: reportId != null
  });

  const all = pages?.pages ?? [];
  const chosen = new Set(selected);

  const setPages = (next: number[]) => onChange(reportId, next);

  const toggle = (pageOrderId: number) =>
    setPages(chosen.has(pageOrderId)
      ? selected.filter(p => p !== pageOrderId)
      : [...selected, pageOrderId]);

  // Changing the report clears the pages rather than keeping the ids: a page id only means
  // anything within its own report, so carrying "1,2,7" across would tick three unrelated pages.
  const pickReport = (value: string) =>
    onChange(value ? Number(value) : null, []);

  if (loadingReports) return <div className="placeholder">Loading KT Insights reports…</div>;

  // Said out loud rather than rendered as an empty picker. "There are no reports" and "we could
  // not ask" are different facts, and only one of them is about the user's account.
  if (reports?.source === 'unavailable') {
    return (
      <div className="notice notice-warn" style={{ marginBottom: 0 }}>
        <strong>KT Insights could not be reached, so the report list is unavailable.</strong>
        <div>
          Existing scheduled exports are unaffected — this only stops new ones being set up. Try
          again shortly.
        </div>
      </div>
    );
  }

  const blocked = entitlement
    ? all.filter(p => !entitlement.deliverable.has(p.pageOrderId)).map(p => p.pageOrderId)
    : [];
  const blockedAndChosen = blocked.filter(id => chosen.has(id));

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div>
        <div className="kti-picker-caption">Report</div>
        <select
          value={reportId ?? ''}
          onChange={e => pickReport(e.target.value)}
          style={{ width: '100%', maxWidth: 420 }}
        >
          <option value="">Choose a report…</option>
          {(reports?.reports ?? []).map(r => (
            <option key={r.reportId} value={r.reportId}>
              {r.reportName} ({r.selectablePageCount} pages)
            </option>
          ))}
        </select>
      </div>

      {reportId == null ? (
        <div className="kti-picker-caption">Pick a report and its pages appear here.</div>
      ) : loadingPages && all.length === 0 ? (
        <div className="placeholder">Loading pages…</div>
      ) : pages?.source === 'unavailable' ? (
        <div className="notice notice-warn" style={{ marginBottom: 0 }}>
          This report's pages could not be read from KT Insights just now. Try again shortly.
        </div>
      ) : all.length === 0 ? (
        <div className="placeholder">This report has no pages that can be exported.</div>
      ) : (
        <>
          <div className="kti-picker-bar">
            <strong>{selected.length}</strong> of {all.length} page{all.length === 1 ? '' : 's'} selected
            {/* What the "Full report" tile used to be. It selects everything the report offers,
                which — unlike that tile's fixed id list — cannot fall behind the report. */}
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => setPages(all.map(p => p.pageOrderId))}>
              Select all {all.length} pages
            </button>
            <button
              type="button"
              className="btn-ghost btn-sm"
              disabled={selected.length === 0}
              onClick={() => setPages([])}>
              Clear
            </button>
          </div>

          {blockedAndChosen.length > 0 && entitlement && (
            // Named, not just counted: "3 pages will be dropped" leaves the user to work out which,
            // and the answer decides whether they care.
            <div className="kti-picker-warning">
              {entitlement.recipientLabel} cannot receive{' '}
              {blockedAndChosen.length === 1 ? 'one selected page' : `${blockedAndChosen.length} selected pages`}
              {' '}— {namesOf(all, blockedAndChosen)}. The export can still be scheduled; KT Insights
              drops {blockedAndChosen.length === 1 ? 'it' : 'them'} when it delivers.
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => setPages(selected.filter(id => !blocked.includes(id)))}>
                Remove {blockedAndChosen.length === 1 ? 'it' : 'them'}
              </button>
            </div>
          )}

          <div className="kti-pages">
            {groupBySection(all).map(group => (
              <div key={group.section} className="kti-page-section">
                <div className="kti-page-section-head">
                  <span>{group.section || 'Pages'}</span>
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    onClick={() => {
                      const ids = group.pages.map(p => p.pageOrderId);
                      const allOn = ids.every(id => chosen.has(id));
                      setPages(allOn
                        ? selected.filter(id => !ids.includes(id))
                        : [...selected, ...ids.filter(id => !chosen.has(id))]);
                    }}>
                    {group.pages.every(p => chosen.has(p.pageOrderId)) ? 'None' : 'All'}
                  </button>
                </div>

                {group.pages.map(page => (
                  <label key={page.pageOrderId} className="kti-page-row">
                    <input
                      type="checkbox"
                      checked={chosen.has(page.pageOrderId)}
                      onChange={() => toggle(page.pageOrderId)}
                    />
                    <span>
                      {page.pageName}
                      {page.viewLabel && page.viewLabel !== page.pageName && (
                        <span className="kti-page-view"> · {page.viewLabel}</span>
                      )}
                      {/*
                        A page the recipient cannot receive stays visible and stays tickable, and
                        both halves of that are deliberate. Hiding it would make the list of pages a
                        property of the recipient rather than of the report, so a page vanishing
                        between two recipients would have no explanation on screen; disabling it
                        would freeze the form around whoever happens to be in the recipient box,
                        which is filled in AFTER this step and changes freely. Saying so and letting
                        the user decide is the only version that survives changing the recipient.
                      */}
                      {entitlement && !entitlement.deliverable.has(page.pageOrderId) && (
                        <span className="kti-page-blocked"> — not available to {entitlement.recipientLabel}</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Which pages the chosen recipient may actually be sent, and what to call them.
 *
 * Resolved once per recipient and report — never per tick — from the same
 * `Embed.PageAccess` check the PBRS pipeline applies before it renders anything. Absent when no
 * recipient has been resolved yet, or when the estate could not be asked: the picker then marks
 * nothing, because "we could not check" must never render as "you cannot have this".
 */
export interface PageEntitlement {
  deliverable: Set<number>;
  recipientLabel: string;
}

function namesOf(pages: KtInsightsReportPage[], ids: number[]) {
  return pages.filter(p => ids.includes(p.pageOrderId)).map(p => p.pageName).join(', ');
}

/**
 * Sections in the order the pages arrive, which is the order PBRS renders them — so the picker
 * reads top to bottom in the same order as the delivered PDF.
 */
function groupBySection(pages: KtInsightsReportPage[]) {
  const groups: { section: string; pages: KtInsightsReportPage[] }[] = [];

  for (const page of pages) {
    const last = groups[groups.length - 1];
    if (last && last.section === page.section) last.pages.push(page);
    else groups.push({ section: page.section, pages: [page] });
  }

  return groups;
}
